import {
  Component,
  Input,
  Output,
  EventEmitter,
  ViewEncapsulation,
  signal,
  HostListener,
  ElementRef,
  inject,
  Injector,
  OnDestroy,
  afterNextRender,
  viewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';

import { CanvasBodyPortalService } from '../../canvas-body-portal.service';
import {
  CANVAS_MARKER_ICON_FILES,
  CANVAS_MARKER_ICON_LABELS,
  markerIconUrl,
} from './canvas-layer-style.utils';

/** Width of the fixed picker popup in px — must match scss. */
const PICKER_WIDTH = 168;

@Component({
  selector: 'lib-canvas-marker-picker',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './canvas-marker-picker.component.html',
  styleUrl: './canvas-marker-picker.component.scss',
  encapsulation: ViewEncapsulation.None,
})
export class CanvasMarkerPickerComponent implements OnDestroy {
  private readonly host = inject(ElementRef<HTMLElement>);
  private readonly injector = inject(Injector);
  private readonly bodyPortal = inject(CanvasBodyPortalService);
  private readonly popupRoot = viewChild<ElementRef<HTMLElement>>('popup');

  private portalDetach?: () => void;

  @Input({ required: true }) iconsBaseUrl!: string;
  @Input() selectedIconId = 0;
  @Input() disabled = false;

  @Output() iconChange = new EventEmitter<number>();

  readonly open = signal(false);
  readonly markerFiles = CANVAS_MARKER_ICON_FILES;
  readonly markerLabels = CANVAS_MARKER_ICON_LABELS;
  /** Fixed viewport coordinates for the popup grid. */
  readonly popupTop = signal(0);
  readonly popupLeft = signal(0);

  iconSrc(iconId: number): string {
    return markerIconUrl(this.iconsBaseUrl, iconId);
  }

  toggle(ev: MouseEvent): void {
    ev.stopPropagation();
    if (this.disabled) return;
    if (this.open()) {
      this.close();
      return;
    }
    const trigger = (ev.currentTarget as HTMLElement);
    const r = trigger.getBoundingClientRect();
    const gap = 6;
    const top = r.bottom + gap;
    const left = Math.min(window.innerWidth - PICKER_WIDTH - 8, Math.max(8, r.right - PICKER_WIDTH));
    this.popupTop.set(top);
    this.popupLeft.set(left);
    this.open.set(true);
    afterNextRender(
      () => {
        if (!this.open()) return;
        const root = this.popupRoot()?.nativeElement;
        if (!root?.parentElement) return;
        this.detachPopupPortal();
        this.portalDetach = this.bodyPortal.attach(root);
      },
      { injector: this.injector },
    );
  }

  select(iconId: number, ev: MouseEvent): void {
    ev.stopPropagation();
    this.iconChange.emit(iconId);
    this.close();
  }

  @HostListener('document:click', ['$event'])
  closeOnOutsideClick(ev: MouseEvent): void {
    if (!this.open()) return;
    const target = ev.target as Node;
    if (this.host.nativeElement.contains(target)) return;
    if (this.popupRoot()?.nativeElement.contains(target)) return;
    this.close();
  }

  @HostListener('window:scroll')
  @HostListener('window:resize')
  onViewportChange(): void {
    if (this.open()) this.close();
  }

  ngOnDestroy(): void {
    this.detachPopupPortal();
  }

  private close(): void {
    this.detachPopupPortal();
    this.open.set(false);
  }

  private detachPopupPortal(): void {
    this.portalDetach?.();
    this.portalDetach = undefined;
  }
}
